import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MemoryIcon from '@mui/icons-material/Memory';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import { Table, TableTitle, Container, Pagination, SearchBar, usePagination } from '../components';
import { useRedirectToAccount } from '../features/user/useRedirectToAccount';
import { useOrganizationsDictStore } from '../features/organizations';
import useRole from '../hooks/use-role';
import {
  useControllersStore,
  useDeviceDefsStore,
  INITIAL_VALUES,
  LIMIT,
  TYPE_OPTIONS,
  getDevicesColumns,
  isForeignOrganization,
} from '../features/controllers';

const Devices = () => {
  const { data, isLoading, error, fetchControllers, total } = useControllersStore();
  const { organizationsById, fetchOrganizations } = useOrganizationsDictStore();
  const { outputsByKey, subtypesByKey, statesByType, fetchDefs } = useDeviceDefsStore();
  const { isMeridianOrg } = useRole();
  const [searchValue, setSearchValue] = useState(INITIAL_VALUES);
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT });
  const totalPages = Math.ceil(total / limit);
  const { handleSwitchToUserAccount } = useRedirectToAccount();

  useEffect(() => {
    fetchControllers({ page, limit, ...searchValue });
  }, [page, limit, searchValue]);

  useEffect(() => {
    fetchDefs();
  }, [fetchDefs]);

  useEffect(() => {
    if (isMeridianOrg) fetchOrganizations();
  }, [isMeridianOrg, fetchOrganizations]);

  const handleSearch = useCallback(
    (value, name) => {
      setSearchValue((prev) => ({ ...prev, [name]: value || '' }));
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  const inputs = useMemo(
    () => [
      {
        type: 'simpleSearch',
        placeholder: 'Поиск по серийному номеру...',
        handleSearch: (value) => handleSearch(value, 'serialId'),
        prefix: <MemoryIcon className="text-gray-500" />,
      },
      {
        type: 'selector',
        placeholder: 'Тип устройства',
        options: TYPE_OPTIONS,
        defaultValue: searchValue.typeKey,
        onChange: (value) => handleSearch(value, 'typeKey'),
        prefix: <DevicesOtherOutlinedIcon className="text-gray-500 mr-2" />,
      },
    ],
    [handleSearch, searchValue.typeKey]
  );

  const columns = useMemo(
    () =>
      getDevicesColumns({
        isMeridianOrg,
        organizationsById,
        handleSwitchToUserAccount,
        typeKey: searchValue.typeKey,
        defs: { subtypesByKey, outputsByKey, statesByType },
      }),
    [handleSwitchToUserAccount, isMeridianOrg, organizationsById, outputsByKey, searchValue.typeKey, statesByType, subtypesByKey]
  );

  return (
    <Container>
      <div className={'max-w-7xl mx-auto'}>
        <TableTitle title={'Устройства'} />
        <SearchBar inputs={inputs} />
      </div>
      <Table
        columns={columns}
        data={data}
        loading={isLoading}
        error={error}
        rowKey="device_id"
        getRowClassName={(row) => (isMeridianOrg && isForeignOrganization(row?.organization_id) ? 'bg-orange-50' : null)}>
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} />}
      </Table>
    </Container>
  );
};

export default Devices;
